/**
 * 時刻表示ユーティリティ — 日本客户向け固定 JST 表示。
 *
 * 規約:
 * - DB / API は UTC(ISO 8601)で返す。タイムゾーンを Asia/Tokyo に変換するのは表示層だけの責務。
 * - 表示は "YYYY-MM-DD HH:mm" 形式(JST サフィックスは付けない;クライアントは日本人で全員が日本時間を見ている前提)。
 * - ブラウザのローカルタイムゾーンに依存しない(Intl.DateTimeFormat の timeZone オプションで固定)。
 * - API が "+00:00" / "Z" を付けない UTC-like 文字列を返した場合も UTC として扱う。
 * - null / undefined / 空文字 / パース失敗は "—" を返す。
 */

/**
 * UTC ISO 文字列(または UTC-like 文字列)を Asia/Tokyo の "YYYY-MM-DD HH:mm" に変換。
 *
 * @param value API/DB から来た時刻文字列(null/undefined 許容)
 * @returns "YYYY-MM-DD HH:mm" または "—"
 */
export function formatJstDateTime(value: string | null | undefined): string {
  if (value === null || value === undefined) return "—";
  const raw = String(value).trim();
  if (!raw) return "—";

  // "YYYY-MM-DDTHH:MM:SS[.fff][Z|+HH:MM]" のいずれか。
  // 末尾に timezone marker が無い UTC-like 文字列(例: "2026-07-03T14:01:51")は
  // 明示的に UTC と解釈して "Z" を補う。
  const hasTz = /(Z|[+-]\d{2}:?\d{2})$/.test(raw);
  const normalized = hasTz ? raw : `${raw}Z`;

  const d = new Date(normalized);
  if (Number.isNaN(d.getTime())) return "—";

  // en-CA ロケールは ISO 8601 形式("YYYY-MM-DD")で日付を返す。
  const datePart = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Tokyo",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(d);

  const timePart = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Asia/Tokyo",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).format(d);

  return `${datePart} ${timePart}`;
}
